import { useEffect, useRef, useCallback } from 'react'
import { useBlocker } from 'react-router-dom'

/**
 * Warns user when navigating away from a page with unsaved changes.
 * Combines browser beforeunload event with React Router's useBlocker.
 *
 * @param hasChanges - Whether the form has unsaved modifications
 * @returns Object with blocker state and skipGuard function
 */
export function useUnsavedChanges(hasChanges: boolean) {
  const skipGuardRef = useRef(false)

  // Browser beforeunload -- handles tab close, URL bar navigation, browser back
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasChanges && !skipGuardRef.current) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasChanges])

  // React Router in-app navigation blocker
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasChanges &&
      !skipGuardRef.current &&
      currentLocation.pathname !== nextLocation.pathname
  )

  // Allow intentional navigation (call before programmatic navigate)
  const skipGuard = useCallback(() => {
    skipGuardRef.current = true
  }, [])

  return { blocker, skipGuard }
}
